import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import FormField from '../../FormField';

import Button from '@/components/ui/button';
import { AuthRoutes } from '@/interfaces/auth/AuthRoutes.enum';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

type PropTypes = {
	refetchUser: () => void;
};

type FormFieldConfig = {
	name: string;
	label: string;
	type: 'text' | 'radio';
	placeholder?: string;
	options?: { label: string; value: string }[];
	containerClassName?: string;
};

const deactivateUserFormFields: FormFieldConfig[] = [
	{
		name: 'isPromotionSubscribed',
		label: 'Do you want to be subscribed to future promotions?',
		type: 'radio',
		options: [
			{ label: 'Yes', value: 'true' },
			{ label: 'No', value: 'false' },
		],
		containerClassName: 'w-full',
	},
	{
		name: 'deactivate',
		label: 'Enter deactivate',
		type: 'text',
		placeholder: 'Enter deactivate',
		containerClassName: 'w-full',
	},
];

const initialValues = {
	isPromotionSubscribed: 'true',
	deactivate: '',
};

const DeactivateUserSchema = yup.object().shape({
	isPromotionSubscribed: yup.string().required(),
	deactivate: yup.string().oneOf(['deactivate'], 'Invalid input').required(),
});

const DeactivateUserForm = ({ refetchUser }: PropTypes) => {
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	const handleSubmit = async (values: {
		isPromotionSubscribed: string;
		deactivate: string;
	}) => {
		try {
			await haggleApiService.deactivateUser({
				isPromotionSubscribed: values.isPromotionSubscribed === 'true',
			});
			notificationService.success('User deactivated successfully');
			navigate(AuthRoutes.SignOut);
			refetchUser();
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to deactivate the user: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="flex flex-col w-3/4 p-4" data-test="deactivate-user-form">
			<Formik
				initialValues={initialValues}
				onSubmit={(values: {
					isPromotionSubscribed: string;
					deactivate: string;
				}) => handleSubmit(values)}
				validationSchema={DeactivateUserSchema}
			>
				{({ values }) => (
					<Form className="flex flex-wrap gap-2 justify-between w-full">
						{deactivateUserFormFields.map((field) => (
							<div key={field.name} className={field.containerClassName || ''}>
								<FormField
									name={field.name}
									label={field.label}
									type={field.type}
									placeholder={field.placeholder}
									options={field.options}
								/>
							</div>
						))}

						<div className="border w-full" />

						<Button
							type="submit"
							color="danger"
							className="border !border-grey-30 disabled:text-black w-full"
							shape="rounded"
							isLoading={isLoading}
							disabled={isLoading || values.deactivate !== 'deactivate'}
							data-test="submit-deactivate-user-button"
						>
							Deactivate Account
						</Button>
					</Form>
				)}
			</Formik>
		</div>
	);
};
export default DeactivateUserForm;
